import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import Cookies from 'js-cookie';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCookie, faCog, faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [cookiePreferences, setCookiePreferences] = useState({
    necessary: true, // Sempre true, non modificabile
    analytics: false,
    marketing: false,
    preferences: false
  });

  useEffect(() => {
    // Controlla se l'utente ha già dato il consenso
    const consent = Cookies.get('cookie-consent');
    if (!consent) {
      setShowBanner(true);
    } else {
      // Carica le preferenze salvate
      const savedPreferences = JSON.parse(consent);
      setCookiePreferences(savedPreferences);
      // IMPORTANTE: Inizializza subito i cookie se il consenso è già stato dato
      initializeCookies(savedPreferences);
    }
  }, []);

  const initializeCookies = (preferences) => {
    // Inizializza Google Analytics se consentito
    if (preferences.analytics && window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted'
      });
    }

    // Inizializza altri servizi di marketing se consentiti
    if (preferences.marketing) {
      // Qui puoi inizializzare Facebook Pixel, Google Ads, etc.
      console.log('Marketing cookies enabled');
    }

    // Inizializza cookie di preferenze se consentiti
    if (preferences.preferences) {
      // Qui puoi salvare preferenze utente come tema, lingua, etc.
      console.log('Preference cookies enabled');
    }
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true
    };
    
    saveCookiePreferences(allAccepted);
    setShowBanner(false);
  };

  const handleRejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false
    };
    
    saveCookiePreferences(onlyNecessary);
    setShowBanner(false);
  };

  const handleCustomize = () => {
    setShowSettings(true);
  };

  const handleSavePreferences = () => {
    saveCookiePreferences(cookiePreferences);
    setShowSettings(false);
    setShowBanner(false);
  };

  const saveCookiePreferences = (preferences) => {
    // Salva le preferenze per 365 giorni
    Cookies.set('cookie-consent', JSON.stringify(preferences), { expires: 365 });
    setCookiePreferences(preferences);
    initializeCookies(preferences);
  };

  const handlePreferenceChange = (type) => {
    if (type === 'necessary') return; // Non permettere di disabilitare i cookie necessari
    
    setCookiePreferences(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  // eslint-disable-next-line no-unused-vars
  const resetCookieConsent = () => {
    Cookies.remove('cookie-consent');
    setShowBanner(true);
    setShowSettings(false);
  };

  if (!showBanner && !showSettings) {
    return (
      // Pulsante per riaprire le impostazioni cookie (sempre visibile)
      <Button
        variant="outline-secondary"
        size="sm"
        className="position-fixed bottom-0 start-0 m-3"
        style={{ zIndex: 1050 }}
        onClick={() => setShowSettings(true)}
        title="Gestisci cookie"
      >
        <FontAwesomeIcon icon={faCookie} />
      </Button>
    );
  }

  return (
    <>
      {/* Banner Cookie */}
      {showBanner && (
        <div 
          className="position-fixed bottom-0 start-0 end-0 bg-dark text-white p-4"
          style={{ zIndex: 1060 }}
        >
          <div className="container">
            <div className="row align-items-center">
              <div className="col-md-8">
                <div className="d-flex align-items-center mb-2">
                  <FontAwesomeIcon icon={faCookie} className="me-2" size="lg" />
                  <h5 className="mb-0">Utilizziamo i cookie</h5>
                </div>
                <p className="mb-0 small">
                  Utilizziamo cookie per migliorare la tua esperienza, analizzare il traffico e personalizzare i contenuti. 
                  Puoi accettare tutti i cookie o personalizzare le tue preferenze.
                </p>
              </div>
              <div className="col-md-4 text-md-end mt-3 mt-md-0">
                <Button 
                  variant="outline-light" 
                  size="sm" 
                  className="me-2 mb-2"
                  onClick={handleRejectAll}
                >
                  <FontAwesomeIcon icon={faTimes} className="me-1" />
                  Rifiuta
                </Button>
                <Button 
                  variant="outline-light" 
                  size="sm" 
                  className="me-2 mb-2"
                  onClick={handleCustomize}
                >
                  <FontAwesomeIcon icon={faCog} className="me-1" />
                  Personalizza
                </Button>
                <Button 
                  variant="success" 
                  size="sm" 
                  className="mb-2"
                  onClick={handleAcceptAll}
                >
                  <FontAwesomeIcon icon={faCheck} className="me-1" />
                  Accetta tutti
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Impostazioni Cookie */}
      <Modal 
        show={showSettings} 
        onHide={() => setShowSettings(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton className="bg-dark text-white">
          <Modal.Title>
            <FontAwesomeIcon icon={faCookie} className="me-2" />
            Impostazioni Cookie
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info">
            I cookie ci aiutano a fornire i nostri servizi. Utilizzando i nostri servizi, 
            accetti il nostro utilizzo dei cookie.
          </Alert>

          <div className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div>
                <h6 className="mb-1">Cookie Necessari</h6>
                <small className="text-muted">
                  Essenziali per il funzionamento del sito. Non possono essere disabilitati.
                </small>
              </div>
              <Form.Check 
                type="switch"
                checked={true}
                disabled={true}
                id="necessary-cookies"
              />
            </div>
          </div>

          <div className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div>
                <h6 className="mb-1">Cookie Analitici</h6>
                <small className="text-muted">
                  Ci aiutano a capire come i visitatori interagiscono con il sito raccogliendo informazioni anonime.
                </small>
              </div>
              <Form.Check 
                type="switch"
                checked={cookiePreferences.analytics}
                onChange={() => handlePreferenceChange('analytics')}
                id="analytics-cookies"
              />
            </div>
          </div>

          <div className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div>
                <h6 className="mb-1">Cookie di Marketing</h6>
                <small className="text-muted">
                  Utilizzati per tracciare i visitatori sui siti web per mostrare annunci pertinenti e coinvolgenti.
                </small>
              </div>
              <Form.Check 
                type="switch"
                checked={cookiePreferences.marketing}
                onChange={() => handlePreferenceChange('marketing')}
                id="marketing-cookies"
              />
            </div>
          </div>

          <div className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div>
                <h6 className="mb-1">Cookie di Preferenze</h6>
                <small className="text-muted">
                  Permettono al sito di ricordare le tue scelte per offrirti funzionalità migliorate e personalizzate.
                </small>
              </div>
              <Form.Check 
                type="switch"
                checked={cookiePreferences.preferences}
                onChange={() => handlePreferenceChange('preferences')}
                id="preference-cookies"
              />
            </div>
          </div>

          <Alert variant="secondary" className="small">
            <strong>Nota:</strong> Disabilitando alcuni cookie potresti limitare alcune funzionalità del sito.
            Puoi modificare queste impostazioni in qualsiasi momento.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowSettings(false)}>
            Annulla
          </Button>
          <Button variant="primary" onClick={handleSavePreferences}>
            Salva Preferenze
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CookieConsent;